import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './GroceryList.css';

/* ── Types ── */

interface GroceryItem {
  name: string;
  quantity: string;
  unit: string;
  source_recipe: string | null;
  checked: boolean;
  removed?: boolean;
  tip?: string | null;
}

interface GroceryCategory {
  category: string;
  items: GroceryItem[];
}

interface UserEdit {
  action: 'add' | 'remove';
  item: string;
  quantity?: string;
  timestamp: string;
  source: string;
}

interface GroceryList {
  nanoid: string;
  status: 'generating' | 'ready' | 'shopping' | 'done';
  items: GroceryCategory[];
  user_edits: UserEdit[];
  sent_at: string | null;
  created_at: string;
  meal_plan_summary?: {
    meal_names: string[];
    order_timing: { day_of_week?: string; time?: string; label?: string } | null;
    cook_timing: Array<{ day_of_week?: string; time?: string }>;
    extras_pills?: string[];
  };
}

/* ── Demo data ── */

const DEMO_LIST: GroceryList = {
  nanoid: 'demo',
  status: 'ready',
  items: [
    { category: 'Produce', items: [
      { name: 'Organic spinach', quantity: '5 oz', unit: '', source_recipe: 'Sheet pan salmon', checked: false },
      { name: 'Cherry tomatoes', quantity: '1 pint', unit: '', source_recipe: 'Chicken stir-fry', checked: false },
      { name: 'Garlic', quantity: '1 head', unit: '', source_recipe: null, checked: false },
      { name: 'Yellow onion', quantity: '2', unit: '', source_recipe: null, checked: false },
    ]},
    { category: 'Protein', items: [
      { name: 'Wild-caught salmon fillet', quantity: '1.5 lbs', unit: '', source_recipe: 'Sheet pan salmon', checked: false, tip: 'Look for wild-caught Pacific salmon — higher in omega-3s than farmed' },
      { name: 'Chicken breast', quantity: '2 lbs', unit: '', source_recipe: 'Chicken stir-fry', checked: false },
    ]},
    { category: 'Dairy', items: [
      { name: 'Organic whole milk', quantity: '1 gallon', unit: '', source_recipe: null, checked: false, tip: 'Choose organic grass-fed for higher omega-3 and CLA content' },
      { name: 'Grass-fed butter', quantity: '1 stick', unit: '', source_recipe: null, checked: false, tip: 'Look for "100% grass-fed" on label (e.g. Kerrygold)' },
    ]},
    { category: 'Eggs', items: [
      { name: 'Eggs', quantity: '1 dozen', unit: '', source_recipe: null, checked: false, tip: 'Pasture-raised eggs have more vitamins — look for "pasture-raised" not just "free-range"' },
    ]},
    { category: 'Pantry', items: [
      { name: 'Extra virgin olive oil', quantity: '1 bottle', unit: '', source_recipe: null, checked: false },
      { name: 'Almond butter', quantity: '1 jar', unit: '', source_recipe: null, checked: false, tip: 'Check label — should contain only almonds and possibly salt, no added sugars or hydrogenated oils' },
    ]},
  ],
  user_edits: [],
  sent_at: null,
  created_at: new Date().toISOString(),
  meal_plan_summary: {
    meal_names: ['Sheet pan salmon with roasted veggies', 'Chicken stir-fry with brown rice'],
    order_timing: { day_of_week: 'saturday', time: '09:00' },
    cook_timing: [{ day_of_week: 'sunday', time: '17:00' }],
    extras_pills: [],
  },
};

/* ── Helpers ── */

function formatTimingLabel(timing: { day_of_week?: string; time?: string } | null | undefined): string {
  if (!timing) return '';
  const parts: string[] = [];
  if (timing.day_of_week) parts.push(capitalize(timing.day_of_week));
  if (timing.time) parts.push(timing.time);
  return parts.join(' at ');
}

function capitalize(s: string): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ── Component ── */

export default function GroceryListPage() {
  const { nanoid } = useParams<{ nanoid: string }>();
  const isDemo = nanoid === 'demo';

  const [list, setList] = useState<GroceryList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  // Add item panel state
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addItemName, setAddItemName] = useState('');
  const [addItemQty, setAddItemQty] = useState('');
  const [addItemCategory, setAddItemCategory] = useState('');

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const apiBase = import.meta.env.VITE_API_URL || '';
  const storageKey = nanoid ? `grocery-checked-${nanoid}` : null;

  /* ── Load list ── */
  async function loadList(silent = false) {
    if (isDemo) {
      setList(DEMO_LIST);
      setOpenCategories(new Set(DEMO_LIST.items.map(c => c.category)));
      setLoading(false);
      return;
    }

    if (!nanoid) return;
    if (!silent) setLoading(true);

    try {
      const r = await fetch(`${apiBase}/api/grocery-lists/${nanoid}`);
      const data = await r.json();

      if (!data.success) {
        setError(data.error || 'Unable to load grocery list.');
        setLoading(false);
        return;
      }

      setList(data.data);

      if (!silent && data.data?.items) {
        setOpenCategories(new Set(data.data.items.map((c: GroceryCategory) => c.category)));
      }
    } catch {
      if (!silent) setError('Network error. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadList(false);
  }, [nanoid]);

  // Restore checked state from localStorage
  useEffect(() => {
    if (!storageKey) return;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try { setCheckedItems(JSON.parse(saved)); } catch {}
    }
  }, [storageKey]);

  // Poll while generating (every 5s)
  useEffect(() => {
    if (list?.status === 'generating') {
      pollRef.current = setInterval(() => loadList(true), 5000);
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [list?.status]);

  /* ── Toggle check ── */
  function toggleCheck(category: string, itemName: string) {
    const key = `${category}::${itemName}`;
    const next = !checkedItems[key];
    const updated = { ...checkedItems, [key]: next };
    setCheckedItems(updated);
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(updated));

    if (!isDemo) {
      fetch(`${apiBase}/api/grocery-lists/${nanoid}/item`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, item_name: itemName, checked: next }),
      }).catch(() => {});
    }
  }

  function toggleCategory(category: string) {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  /* ── Remove item ── */
  function removeItem(category: string, itemName: string) {
    setList(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map(cat =>
          cat.category === category
            ? { ...cat, items: cat.items.map(item => item.name === itemName ? { ...item, removed: true } : item) }
            : cat
        ),
      };
    });

    if (!isDemo) {
      fetch(`${apiBase}/api/grocery-lists/${nanoid}/edit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'remove', category, item_name: itemName }),
      }).catch(() => {});
    }
  }

  /* ── Add item ── */
  function addItem() {
    const name = addItemName.trim();
    if (!name) return;

    const category = addItemCategory || (list?.items[0]?.category ?? 'Other');
    const newItem: GroceryItem = { name, quantity: addItemQty.trim(), unit: '', source_recipe: null, checked: false };

    setList(prev => {
      if (!prev) return prev;
      const catIdx = prev.items.findIndex(c => c.category === category);
      let newItems: GroceryCategory[];
      if (catIdx >= 0) {
        newItems = prev.items.map((cat, i) =>
          i === catIdx ? { ...cat, items: [...cat.items, newItem] } : cat
        );
      } else {
        newItems = [...prev.items, { category, items: [newItem] }];
      }
      return { ...prev, items: newItems };
    });

    // Ensure the category is open
    setOpenCategories(prev => { const s = new Set(prev); s.add(category); return s; });

    if (!isDemo) {
      fetch(`${apiBase}/api/grocery-lists/${nanoid}/edit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add', category, item_name: name, quantity: addItemQty.trim() }),
      }).catch(() => {});
    }

    setAddItemName('');
    setAddItemQty('');
    setShowAddPanel(false);
  }

  /* ── Loading / Error ── */
  if (loading) {
    return (
      <div className="grocery-list-page">
        <PublicHeader />
        <div className="grocery-list-container">
          <div className="gl-loading">Loading your grocery list…</div>
        </div>
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="grocery-list-page">
        <PublicHeader />
        <div className="grocery-list-container">
          <div className="gl-error">{error || 'Grocery list not found.'}</div>
        </div>
      </div>
    );
  }

  const summary = list.meal_plan_summary;
  const totalItems = list.items.reduce((acc, cat) => acc + cat.items.filter(i => !i.removed).length, 0);
  const checkedCount = list.items.reduce((acc, cat) =>
    acc + cat.items.filter(i => !i.removed && checkedItems[`${cat.category}::${i.name}`]).length, 0
  );

  const categoryNames = list.items.map(c => c.category);

  return (
    <div className="grocery-list-page">
      <PublicHeader />
      <div className="grocery-list-container">

        {isDemo && (
          <div className="generating-banner" style={{ background: '#fff8e1', borderColor: '#f5c400', color: '#7a5800' }}>
            Demo mode — no real data, changes are local only
          </div>
        )}

        {/* Header */}
        <div className="grocery-header">
          <h1>Grocery List</h1>
          {summary?.meal_names && summary.meal_names.length > 0 && (
            <p className="meal-summary">
              {summary.meal_names.slice(0, 3).join(', ')}
              {summary.meal_names.length > 3 ? ` + ${summary.meal_names.length - 3} more` : ''}
            </p>
          )}
          <div className="timing-chips">
            {summary?.order_timing && formatTimingLabel(summary.order_timing) && (
              <span className="timing-chip">
                Shopping: {formatTimingLabel(summary.order_timing)}
              </span>
            )}
            {summary?.cook_timing && summary.cook_timing.filter(ct => ct.day_of_week).map((ct, i) => (
              <span key={i} className="timing-chip">
                Cook: {formatTimingLabel(ct)}
              </span>
            ))}
            {totalItems > 0 && (
              <span className="timing-chip">
                {checkedCount}/{totalItems} checked
              </span>
            )}
          </div>
        </div>

        {/* Generating banner */}
        {list.status === 'generating' && (
          <div className="generating-banner">
            Building your list… this usually takes less than a minute.
          </div>
        )}

        {/* Categories */}
        {list.items.map(cat => {
          const visibleItems = cat.items.filter(i => !i.removed);
          if (visibleItems.length === 0) return null;
          const isOpen = openCategories.has(cat.category);
          const catChecked = visibleItems.filter(i => checkedItems[`${cat.category}::${i.name}`]).length;

          return (
            <div key={cat.category} className="category-section">
              <div className="category-header" onClick={() => toggleCategory(cat.category)}>
                <div className="category-title">
                  {cat.category}
                  <span className="category-count">
                    {catChecked > 0 ? `${catChecked}/` : ''}{visibleItems.length}
                  </span>
                </div>
                <span className={`category-chevron${isOpen ? ' open' : ''}`}>▼</span>
              </div>

              {isOpen && (
                <div className="category-items">
                  {visibleItems.map(item => {
                    const checked = Boolean(checkedItems[`${cat.category}::${item.name}`]);
                    return (
                      <div
                        key={item.name}
                        className={`grocery-item${checked ? ' checked' : ''}`}
                        onClick={() => toggleCheck(cat.category, item.name)}
                      >
                        <input
                          type="checkbox"
                          className="grocery-item-check"
                          checked={checked}
                          onChange={() => toggleCheck(cat.category, item.name)}
                          onClick={e => e.stopPropagation()}
                        />
                        <div className="grocery-item-info">
                          <div className="grocery-item-name">{item.name}</div>
                          {(item.quantity || item.unit) && (
                            <div className="grocery-item-qty">
                              {[item.quantity, item.unit].filter(Boolean).join(' ')}
                            </div>
                          )}
                          {item.tip && (
                            <div className="grocery-item-tip">{item.tip}</div>
                          )}
                        </div>
                        {item.source_recipe && item.source_recipe !== 'staples' && item.source_recipe !== 'extras' && (
                          <div className="grocery-item-source">{item.source_recipe}</div>
                        )}
                        <button
                          className="grocery-item-remove"
                          onClick={e => { e.stopPropagation(); removeItem(cat.category, item.name); }}
                          aria-label={`Remove ${item.name}`}
                        >✕</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Add item panel */}
        {showAddPanel ? (
          <div className="add-item-panel">
            <div className="add-item-panel-title">Add item</div>
            <input
              className="add-item-input"
              type="text"
              placeholder="Item name (e.g. Organic apples)"
              value={addItemName}
              onChange={e => setAddItemName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              autoFocus
            />
            <div className="add-item-row">
              <input
                className="add-item-qty"
                type="text"
                placeholder="Qty (optional)"
                value={addItemQty}
                onChange={e => setAddItemQty(e.target.value)}
              />
              <select
                className="add-item-category"
                value={addItemCategory}
                onChange={e => setAddItemCategory(e.target.value)}
              >
                {categoryNames.map(c => <option key={c} value={c}>{c}</option>)}
                {!categoryNames.includes('Other') && <option value="Other">Other</option>}
              </select>
            </div>
            <div className="add-item-actions">
              <button className="btn-add-item-confirm" onClick={addItem} disabled={!addItemName.trim()}>
                Add to list
              </button>
              <button className="btn-add-item-cancel" onClick={() => { setShowAddPanel(false); setAddItemName(''); setAddItemQty(''); }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="btn-show-add-item" onClick={() => { setShowAddPanel(true); setAddItemCategory(categoryNames[0] ?? 'Other'); }}>
            + Add item
          </button>
        )}

        {/* User edits log */}
        {list.user_edits && list.user_edits.length > 0 && (
          <div className="user-edits-section">
            <div className="user-edits-header">Your changes</div>
            {list.user_edits.map((edit, i) => (
              <div key={i} className="user-edit-row">
                <span className={`edit-badge ${edit.action}`}>
                  {edit.action === 'add' ? '+' : '−'}
                </span>
                <span>
                  {edit.quantity ? `${edit.quantity} ` : ''}{edit.item}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="grocery-footer">
          {isDemo ? 'Demo list — text Mosaime to get your own grocery list.' : 'Text Mosaime to add or remove items from your list.'}
          <br />
          <span style={{ fontSize: '0.75rem', color: '#aaa' }}>
            List created {new Date(list.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>

      </div>
    </div>
  );
}
